import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DropzoneConfigInterface, DropzoneDirective } from 'ngx-dropzone-wrapper';
import { IMAGE_FILE_FORMATS, UiContextService } from '@cakeiteasy/web-utils';
import { TranslateService } from '@ngx-translate/core';
import { IAspectRatioInfo, IMAGE_MB_SIZE_LIMIT, MOTIVE_MAX_IMAGE_SIZE_PX } from '../../../constants/common.constants';
import { ImageCropperExtraService } from '../../../../image-cropper/image-cropper-extra.service';

@Component({
  standalone: false,
  selector: 'ui-choose-motive-cropper',
  templateUrl: './ui-choose-motive-cropper.component.html',
  styleUrls: ['./ui-choose-motive-cropper.component.scss'],
})
export class UiChooseMotiveCropperComponent implements OnInit {
  @Input() imgUrl: string;
  @Input() enableResize = false;
  @Input() aspectRatios: IAspectRatioInfo[];
  @Input() selectedAspectRatio: IAspectRatioInfo;
  @Input() keepBgTransparent = false;
  @Input() extendedViewMode: boolean;
  @Input() fitIntoAspectRatio = false;
  @Input() file: File;
  @Input() maxImageSize? = MOTIVE_MAX_IMAGE_SIZE_PX;
  @Input() imagesForChooseSrc: string[];

  @Output() onImgUrlChanged = new EventEmitter<string>();
  @Output() onRatioChanged = new EventEmitter();
  @Output() onResultUrlChanged = new EventEmitter<string>();
  @Output() onFileChosen = new EventEmitter<File>();
  @ViewChild(DropzoneDirective, { static: false }) myDropzone?: DropzoneDirective;

  public imageFormat: IMAGE_FILE_FORMATS = IMAGE_FILE_FORMATS.JPG;

  public fileValidationError: string = null;
  public resultImgUrl: string;
  public busy: Promise<any>;

  public initialFitIntoAspectRatio: boolean;
  public initialSelectedAspectRatio: IAspectRatioInfo = null;

  public dropZoneConfig: DropzoneConfigInterface = {
    url: 'null',
    clickable: false,
    autoProcessQueue: false,
    autoQueue: false,
    createImageThumbnails: false,
    acceptedFiles: 'image/*, image/heif, image/heic',
  };

  private _imageMbSizeLimit = IMAGE_MB_SIZE_LIMIT;

  constructor(
    public uiContext: UiContextService,
    private _translate: TranslateService,
    private _imageService: ImageCropperExtraService,
  ) {}

  ngOnInit(): void {
    this.initialSelectedAspectRatio = this.selectedAspectRatio;
    this.initialFitIntoAspectRatio = this.fitIntoAspectRatio;

    this.aspectRatios = this.filterAspectRatios(this.aspectRatios);
  }

  onCropped(url: string) {
    this.onResultUrlChanged.emit(url);
    this.resultImgUrl = url;
  }

  fileChosen(file) {
    this.onFileChosen.emit(file);

    this.imageFormat = file.type;
    this.busy = this._imageService
      .prepareImageForCropping(file, this.imageFormat, this.keepBgTransparent, this.maxImageSize)
      .then((src) => {
        this.imgUrl = src;

        this.changeAspectRatio(this.initialSelectedAspectRatio, true);

        this.onImgUrlChanged.emit(this.imgUrl);
      })
      .catch((err) => {
        console.log('error with image cropping');
        //TODO: remove
        throw new Error(JSON.stringify(err));
      });
  }

  public async onFileDropped($event: DragEvent) {
    this.fileValidationError = null;
    $event.preventDefault();
    let file = $event.dataTransfer.files[0];

    return this._imageFileProcess(file);
  }

  public async onFileInputChanged($event) {
    this.fileValidationError = null;
    let file = $event.target.files[0];

    return this._imageFileProcess(file);
  }

  public changeAspectRatio(aspectRatio: any, forceFitIntoAspectRatio: boolean = false) {
    this.selectedAspectRatio = aspectRatio;
    if (forceFitIntoAspectRatio) {
      this.fitIntoAspectRatio = this.initialFitIntoAspectRatio;
    }
    this.onRatioChanged.emit(aspectRatio);
  }

  public selectImagesForChoose(src: string) {
    this.fileValidationError = null;
    this.imgUrl = src;

    this.changeAspectRatio(this.initialSelectedAspectRatio, true);

    this.onImgUrlChanged.emit(this.imgUrl);
  }

  private filterAspectRatios(aspectRatios: IAspectRatioInfo[]): IAspectRatioInfo[] {
    return this.isBrokenChromeVersion() ? aspectRatios.filter(({ isCircle }) => !isCircle) : aspectRatios;
  }

  /**Temporary solution before new image cropper will be used*/
  private isBrokenChromeVersion() {
    const ua = navigator.userAgent;
    const match = ua.match(/Chrome\/(\d+)/);

    if (!match) {
      return false;
    }

    const chromeVersion = parseInt(match[1], 10);
    return chromeVersion >= 124 && chromeVersion <= 140;
  }

  private _checkIfFileHeicFormat(file: File): boolean {
    return /\.(heic|heif)$/i.test(file.name.toLowerCase());
  }

  private async _imageFileProcess(file) {
    if (!file) {
      return false;
    }

    if (!/image\//.test(file.type) && !this._checkIfFileHeicFormat(file)) {
      this.fileValidationError = this._translate.instant('cakeBuilder.cakeMotiveSection.uploadImage.wrongFormat');
      return this._resetDropZone();
    } else if (file && file.size > this._imageMbSizeLimit) {
      this.fileValidationError = this._translate.instant(
        'cakeBuilder.cakeMotiveSection.uploadImage.fileSizeLimitError',
      );
      return this._resetDropZone();
    }

    if (this._checkIfFileHeicFormat(file)) {
      const response = await (this.busy = this._imageService.convertHeic(file));
      file = response.body;
    }

    return this.fileChosen(file);
  }

  private _resetDropZone() {
    if (this.myDropzone) {
      this.myDropzone.reset(true);
    }
  }
}
